/**
 * 司機日報表頁面
 * 查詢司機的任務日報表，預設顯示當日托運紀錄
 */

import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLiff } from '@/contexts/LiffContext'
import { getDriver } from '@/services/driverService'
import { getDailyReport, getTaskDetail } from '@/services/dailyReportService'
import type {
  Driver,
  Vehicle,
  VehicleState,
  DailyReportTaskItem,
  TaskDetailResponse,
} from '@/types'
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Fab,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation'
import SpeedIcon from '@mui/icons-material/Speed'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { zhTW } from 'date-fns/locale'
import {
  FormControl,
  Select,
  MenuItem,
  // 查詢類型選單暫時隱藏，保留以備未來使用
  // FormControlLabel,
  // Radio,
  // RadioGroup,
} from '@mui/material'

interface DailyReport {
  queryRange: {
    startDate: string
    endDate: string
    isSingleDay: boolean
  }
  summary: {
    totalTasks: number
    localTasks: number
    crossAreaTasks: number
    driveMileage: number | null
    oilConsumption: number | null
    avgOil: number | null
    // 本地 interface 漏了此欄，但畫面（頁首、匯出）與 service 都在用，tsc 會擋建置
    vehicleNos: string[] | null
  }
  tasks: DailyReportTaskItem[]
  pagination: {
    page: number
    pageSize: number
    totalItems: number
    totalPages: number
  }
}

const DailyReportPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { lineUser } = useLiff()

  // 從 location.state 取得資料
  const stateDriver = location.state?.driver as Driver | undefined
  const stateVehicle = location.state?.vehicle as Vehicle | undefined
  const stateVehicleState = location.state?.vehicleState as VehicleState | undefined
  const autoQuery = location.state?.autoQuery as boolean | undefined
  const fromHome = location.state?.fromHome as boolean | undefined
  const stateReportDate = location.state?.reportDate as string | undefined  // 班別日期 (yyyy-MM-dd)

  // 從 URL 參數取得今日查詢標記
  const searchParams = new URLSearchParams(location.search)
  const todayParam = searchParams.get('today') === 'true'

  // ========== State ==========
  const [driver, setDriver] = useState<Driver | null>(stateDriver || null)
  const [vehicle, setVehicle] = useState<Vehicle | null>(stateVehicle || null)
  const [loading, setLoading] = useState(true)

  // 查詢類型：dutyDate(班別日期), acceptedTime(接單時間), completedTime(完成任務時間)
  // 目前僅開放班別日期查詢，其他選項暫時隱藏
  const [queryType, _setQueryType] = useState<'dutyDate' | 'acceptedTime' | 'completedTime'>('dutyDate')

  // 預設日期時間區間為最近7天，時間為 00:00 到 23:59
  const [startDate, setStartDate] = useState<Date | null>(subDays(new Date(), 6))
  const [startHour, setStartHour] = useState<string>('00')
  const [startMinute, setStartMinute] = useState<string>('00')
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [endHour, setEndHour] = useState<string>('23')
  const [endMinute, setEndMinute] = useState<string>('59')

  const [report, setReport] = useState<DailyReport | null>(null)
  const [selectedTask, setSelectedTask] = useState<TaskDetailResponse | null>(null)
  const [taskDetailDialogOpen, setTaskDetailDialogOpen] = useState(false)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [queriedIsSingleDay, setQueriedIsSingleDay] = useState<boolean>(false) // 記錄查詢時是否為單日，預設為多日
  const [searchExpanded, setSearchExpanded] = useState(true) // 搜尋區塊是否展開
  const [selectedQuickOption, setSelectedQuickOption] = useState<'today' | 'yesterday' | 'last7days' | 'thisWeek' | 'lastWeek' | 'thisMonth' | null>('last7days') // 預設為最近7天

  // 分頁相關 state
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [rowsPerPageDialogOpen, setRowsPerPageDialogOpen] = useState(false)
  const [pageSelectDialogOpen, setPageSelectDialogOpen] = useState(false)

  // 浮動按鈕位置 state
  const [fabPosition, setFabPosition] = useState<'left' | 'right'>(() => {
    // 從 localStorage 讀取用戶偏好
    const saved = localStorage.getItem('fabPosition')
    return (saved === 'left' || saved === 'right') ? saved : 'right'
  })
  const [fabY, setFabY] = useState<number>(() => {
    const saved = localStorage.getItem('fabY')
    return saved ? parseInt(saved) : 120
  })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, fabY: 120, fabX: 0 })
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 })

  // ========== 載入資料 ==========
  useEffect(() => {
    const loadData = async () => {
      if (!lineUser) return

      try {
        // 確認司機資料
        const driverData = await getDriver(lineUser.userId)
        if (!driverData) {
          navigate('/binding', { replace: true })
          return
        }

        setDriver(stateDriver || driverData)
        setVehicle(stateVehicle || null)

        // 如果有自動查詢標記或 URL 參數 today=true，執行當日查詢
        if (autoQuery || todayParam) {
          // 如果有傳入班別日期（交班後跳轉），使用班別日期；否則使用今天
          const queryDate = stateReportDate ? new Date(stateReportDate) : new Date()
          setStartDate(queryDate)
          setStartHour('00')
          setStartMinute('00')
          setEndDate(queryDate)
          setEndHour('23')
          setEndMinute('59')
          setQueriedIsSingleDay(true)
          setSelectedQuickOption(stateReportDate ? null : 'today')  // 使用班別日期時不選快捷選項
          // 傳入日期以避免 setState 非同步問題
          loadDailyReport(queryDate, queryDate)
          // 自動查詢後收起搜尋區塊
          setSearchExpanded(false)
        }
        // 否則初始載入時不自動查詢，等待用戶按下查詢按鈕
      } catch (error) {
        console.error('載入資料失敗:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [lineUser, navigate])

  // ========== 載入日報表 ==========
  const loadDailyReport = async (overrideStartDate?: Date, overrideEndDate?: Date) => {
    const queryStartDate = overrideStartDate || startDate
    const queryEndDate = overrideEndDate || endDate

    if (!queryStartDate || !queryEndDate) return

    console.log('查詢類型:', queryType)
    console.log('開始日期:', format(queryStartDate, 'yyyy-MM-dd'), '時間:', `${startHour}:${startMinute}`)
    console.log('結束日期:', format(queryEndDate, 'yyyy-MM-dd'), '時間:', `${endHour}:${endMinute}`)

    try {
      setLoading(true)
      const response = await getDailyReport({
        startDate: format(queryStartDate, 'yyyy-MM-dd'),
        endDate: format(queryEndDate, 'yyyy-MM-dd'),
        startTime: `${startHour}:${startMinute}`,
        endTime: `${endHour}:${endMinute}`,
        queryType,
        page: page + 1, // API 使用 1-based page
        pageSize: rowsPerPage,
      })

      setReport(response)
      setQueriedIsSingleDay(response.queryRange.isSingleDay)
    } catch (error) {
      console.error('載入日報表失敗:', error)
    } finally {
      setLoading(false)
    }
  }

  // ========== 快捷日期選擇 ==========
  const handleQuickDateSelect = (type: 'today' | 'yesterday' | 'last7days' | 'thisWeek' | 'lastWeek' | 'thisMonth') => {
    const today = new Date()
    let start: Date
    let end: Date

    switch (type) {
      case 'today':
        start = today
        end = today
        break
      case 'yesterday':
        start = subDays(today, 1)
        end = subDays(today, 1)
        break
      case 'last7days':
        start = subDays(today, 6)
        end = today
        break
      case 'thisWeek':
        start = startOfWeek(today, { weekStartsOn: 1 })
        end = endOfWeek(today, { weekStartsOn: 1 })
        break
      case 'lastWeek':
        const lastWeek = subDays(today, 7)
        start = startOfWeek(lastWeek, { weekStartsOn: 1 })
        end = endOfWeek(lastWeek, { weekStartsOn: 1 })
        break
      case 'thisMonth':
        start = startOfMonth(today)
        end = endOfMonth(today)
        break
      default:
        start = today
        end = today
    }

    setStartDate(start)
    setStartHour('00')
    setStartMinute('00')
    setEndDate(end)
    setEndHour('23')
    setEndMinute('59')
    setSelectedQuickOption(type) // 更新選中的快速選項
  }

  // ========== 查詢日報表 ==========
  const handleSearch = () => {
    // 記錄查詢時是否為單日（比較日期字串）
    if (startDate && endDate) {
      const isSame = format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')
      setQueriedIsSingleDay(isSame)
      loadDailyReport()
      // 查詢後自動收起搜尋區塊
      setSearchExpanded(false)
    }
  }

  // ========== 查詢類型變更 - 目前僅開放班別日期，暫時隱藏 ==========
  // const handleQueryTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setQueryType(event.target.value as 'dutyDate' | 'acceptedTime' | 'completedTime')
  // }

  // ========== 打開任務詳情 ==========
  const handleOpenTaskDetail = async (task: DailyReportTaskItem) => {
    // MY（船邊櫃）任務不開啟明細視窗
    if (task.taskType === 'MY') {
      return
    }
    // v2.3：補登審核中任務唯讀，不開詳情
    if (task.source === 'AMENDMENT_PENDING') {
      return
    }

    setTaskDetailDialogOpen(true)
    setLoadingDetail(true)
    setSelectedTask(null)

    try {
      const detail = await getTaskDetail(task.taskId)
      setSelectedTask(detail)
    } catch (error) {
      console.error('載入任務詳情失敗:', error)
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleCloseTaskDetail = () => {
    setTaskDetailDialogOpen(false)
    setSelectedTask(null)
  }

  // ========== 分頁處理 ==========
  const handleChangePage = async (_event: unknown, newPage: number) => {
    setPage(newPage)
    // 呼叫 API 取得該頁資料
    if (startDate && endDate) {
      try {
        setLoading(true)
        const response = await getDailyReport({
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          startTime: `${startHour}:${startMinute}`,
          endTime: `${endHour}:${endMinute}`,
          queryType,
          page: newPage + 1, // API 使用 1-based page
          pageSize: rowsPerPage,
        })
        setReport(response)
        // 延遲滾動，確保頁面內容已更新
        setTimeout(() => {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth',
          })
        }, 100)
      } catch (error) {
        console.error('載入日報表失敗:', error)
      } finally {
        setLoading(false)
      }
    }
  }

  // ========== 滾動到頂部 ==========
  const handleScrollToTop = () => {
    if (!isDragging) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  // ========== 浮動按鈕拖動處理 ==========
  const handleFabTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    setIsDragging(false)
    // 計算當前按鈕的實際 X 位置
    const currentX = fabPosition === 'left' ? 16 + 22 : window.innerWidth - 16 - 22 // 16 是 padding，22 是按鈕半徑
    setDragStart({
      x: touch.clientX,
      y: touch.clientY,
      fabY: fabY,
      fabX: currentX,
    })
  }

  const handleFabTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0]
    const deltaX = touch.clientX - dragStart.x
    const deltaY = touch.clientY - dragStart.y

    // 如果移動超過 5px，視為拖動
    if (!isDragging && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
      setIsDragging(true)
    }

    if (isDragging || Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      setIsDragging(true)

      // 計算新的 X 位置
      const newX = dragStart.fabX + deltaX
      // 限制 X 範圍：至少 22px（按鈕半徑），最多到螢幕寬度 - 22px
      const clampedX = Math.max(22, Math.min(window.innerWidth - 22, newX))

      // 計算新的 Y 位置（從底部算起）
      const newY = dragStart.fabY - deltaY
      // 限制 Y 範圍：不要超出螢幕
      const maxY = window.innerHeight - 60
      const minY = 60
      const clampedY = Math.max(minY, Math.min(maxY, newY))

      setDragPosition({ x: clampedX, y: clampedY })
      setFabY(clampedY)
    }
  }

  const handleFabTouchEnd = (_e: React.TouchEvent) => {
    if (isDragging) {
      const finalX = dragPosition.x || dragStart.fabX

      // 判斷應該吸附到左邊還是右邊（以螢幕中央為分界）
      const screenWidth = window.innerWidth
      const newPosition = finalX < screenWidth / 2 ? 'left' : 'right'

      setFabPosition(newPosition)
      localStorage.setItem('fabPosition', newPosition)
      localStorage.setItem('fabY', fabY.toString())

      // 重置拖動位置
      setDragPosition({ x: 0, y: 0 })

      // 延遲重置 isDragging，避免觸發 onClick
      setTimeout(() => setIsDragging(false), 300)
    } else {
      setIsDragging(false)
    }
  }

  // ========== 返回 ==========
  const handleBack = () => {
    // 如果是從交班頁面或首頁進入，返回首頁
    if (autoQuery || fromHome) {
      navigate('/home', {
        state: {
          driver: stateDriver,
          vehicle: stateVehicle,
          vehicleState: stateVehicleState,
        },
      })
    } else {
      // 否則返回任務列表
      navigate('/tasks', {
        state: {
          driver: stateDriver,
          vehicle: stateVehicle,
          vehicleState: stateVehicleState,
        },
      })
    }
  }

  // 取得返回按鈕文字
  const getBackButtonText = () => {
    if (autoQuery) return '返回首頁'
    if (fromHome) return '返回首頁'
    return '返回任務列表'
  }

  // ========== 載入中 ==========
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#6B705C' }} />
        <Typography variant="body1" sx={{ mt: 2, color: '#6B705C' }}>
          載入中...
        </Typography>
      </Box>
    )
  }

  // ========== Render ==========
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
        py: 1.5,
      }}
    >
      <Container maxWidth="md">
        {/* 返回按鈕 */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{
            mb: 1,
            color: '#6B705C',
            '&:hover': {
              backgroundColor: 'rgba(107, 112, 92, 0.08)',
            },
          }}
        >
          {getBackButtonText()}
        </Button>

        {/* 頁面標題 */}
        <Paper
          elevation={2}
          sx={{
            p: 1.5,
            mb: 1.5,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #6B705C 0%, #A5A58D 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssignmentIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {autoQuery ? '日報表' : '歷史紀錄'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {driver?.driverName} · {report?.summary?.vehicleNos?.length ? report.summary.vehicleNos.join(', ') : vehicle?.vehicleNo || '未選擇車輛'}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 日期時間查詢 - 日報表模式下隱藏 */}
        {!autoQuery && (
        <Paper elevation={2} sx={{ p: 1.5, mb: 1.5, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: '#6B705C', display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <CalendarTodayIcon sx={{ fontSize: 20 }} />
              查詢條件
            </Typography>
            <Box
              onClick={() => setSearchExpanded(!searchExpanded)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                color: '#6B705C',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {searchExpanded ? '隱藏搜尋' : '展開搜尋'}
              </Typography>
              <IconButton sx={{ color: '#6B705C', p: 0.5 }} size="small">
                {searchExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>

          <Collapse in={searchExpanded}>
            {/* 查詢類型選擇 - 目前僅開放班別日期，暫時隱藏選單 */}
            {/* <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                查詢類型：
              </Typography>
              <RadioGroup row value={queryType} onChange={handleQueryTypeChange}>
                <FormControlLabel
                  value="dutyDate"
                  control={
                    <Radio
                      sx={{
                        color: '#6B705C',
                        '&.Mui-checked': {
                          color: '#6B705C',
                        },
                      }}
                    />
                  }
                  label="班別日期"
                />
                <FormControlLabel
                  value="acceptedTime"
                  control={
                    <Radio
                      sx={{
                        color: '#6B705C',
                        '&.Mui-checked': {
                          color: '#6B705C',
                        },
                      }}
                    />
                  }
                  label="接單時間"
                />
                <FormControlLabel
                  value="completedTime"
                  control={
                    <Radio
                      sx={{
                        color: '#6B705C',
                        '&.Mui-checked': {
                          color: '#6B705C',
                        },
                      }}
                    />
                  }
                  label="完成時間"
                />
              </RadioGroup>
            </Box> */}

            {/* 快捷日期選擇 */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                快速選擇：
              </Typography>
              <Box sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                overflowY: 'hidden',
                pb: 1,
                '&::-webkit-scrollbar': {
                  height: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '3px',
                },
              }}>
              <Chip
                label="今日"
                onClick={() => handleQuickDateSelect('today')}
                sx={{
                  backgroundColor: selectedQuickOption === 'today' ? '#6B705C' : '#A5A58D',
                  color: 'white',
                  '&:hover': { backgroundColor: selectedQuickOption === 'today' ? '#5A5F4A' : '#8E8E76' },
                  fontWeight: 600,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              />
              <Chip
                label="昨日"
                onClick={() => handleQuickDateSelect('yesterday')}
                sx={{
                  backgroundColor: selectedQuickOption === 'yesterday' ? '#6B705C' : '#A5A58D',
                  color: 'white',
                  '&:hover': { backgroundColor: selectedQuickOption === 'yesterday' ? '#5A5F4A' : '#8E8E76' },
                  fontWeight: 600,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              />
              <Chip
                label="最近7天"
                onClick={() => handleQuickDateSelect('last7days')}
                sx={{
                  backgroundColor: selectedQuickOption === 'last7days' ? '#6B705C' : '#A5A58D',
                  color: 'white',
                  '&:hover': { backgroundColor: selectedQuickOption === 'last7days' ? '#5A5F4A' : '#8E8E76' },
                  fontWeight: 600,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              />
              <Chip
                label="本週"
                onClick={() => handleQuickDateSelect('thisWeek')}
                sx={{
                  backgroundColor: selectedQuickOption === 'thisWeek' ? '#6B705C' : '#A5A58D',
                  color: 'white',
                  '&:hover': { backgroundColor: selectedQuickOption === 'thisWeek' ? '#5A5F4A' : '#8E8E76' },
                  fontWeight: 600,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              />
              <Chip
                label="上週"
                onClick={() => handleQuickDateSelect('lastWeek')}
                sx={{
                  backgroundColor: selectedQuickOption === 'lastWeek' ? '#6B705C' : '#A5A58D',
                  color: 'white',
                  '&:hover': { backgroundColor: selectedQuickOption === 'lastWeek' ? '#5A5F4A' : '#8E8E76' },
                  fontWeight: 600,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              />
              <Chip
                label="本月"
                onClick={() => handleQuickDateSelect('thisMonth')}
                sx={{
                  backgroundColor: selectedQuickOption === 'thisMonth' ? '#6B705C' : '#A5A58D',
                  color: 'white',
                  '&:hover': { backgroundColor: selectedQuickOption === 'thisMonth' ? '#5A5F4A' : '#8E8E76' },
                  fontWeight: 600,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              />
            </Box>
          </Box>

            {/* 自訂日期時間輸入 */}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={zhTW}>
              {/* 開始日期時間 */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {queryType === 'dutyDate' ? '開始日期：' : '開始時間：'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* 日期選擇器 */}
                  <Box sx={{ width: queryType === 'dutyDate' ? '100%' : '47%', flexShrink: 0 }}>
                    <MobileDatePicker
                      label="日期"
                      value={startDate}
                      onChange={(newValue) => {
                        setStartDate(newValue)
                        setSelectedQuickOption(null) // 手動修改日期時清除快速選擇
                      }}
                      format="yyyy/MM/dd"
                      views={['year', 'month', 'day']}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          sx: {
                            '& .MuiInputBase-input': {
                              padding: '6px 8px',
                              fontSize: '0.875rem',
                            },
                          },
                        },
                        toolbar: {
                          toolbarFormat: 'yyyy/MM/dd',
                        },
                        calendarHeader: {
                          format: 'yyyy/MM',
                        },
                      }}
                    />
                  </Box>

                  {/* 時間選擇器 - 班別日期查詢時隱藏 */}
                  {queryType !== 'dutyDate' && (
                  <Box sx={{ flex: 1, display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                      <Select
                        value={startHour}
                        onChange={(e) => setStartHour(e.target.value)}
                        displayEmpty
                        sx={{ minWidth: 0 }}
                      >
                        {Array.from({ length: 24 }, (_, i) => {
                          const hour = i.toString().padStart(2, '0')
                          return <MenuItem key={hour} value={hour}>{hour}</MenuItem>
                        })}
                      </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, fontSize: '1.2rem', fontWeight: 600, flexShrink: 0 }}>:</Box>
                    <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                      <Select
                        value={startMinute}
                        onChange={(e) => setStartMinute(e.target.value)}
                        displayEmpty
                        sx={{ minWidth: 0 }}
                      >
                        {Array.from({ length: 60 }, (_, i) => {
                          const minute = i.toString().padStart(2, '0')
                          return <MenuItem key={minute} value={minute}>{minute}</MenuItem>
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                  )}
                </Box>
              </Box>

              {/* 結束日期時間 */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {queryType === 'dutyDate' ? '結束日期：' : '結束時間：'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* 日期選擇器 */}
                  <Box sx={{ width: queryType === 'dutyDate' ? '100%' : '47%', flexShrink: 0 }}>
                    <MobileDatePicker
                      label="日期"
                      value={endDate}
                      onChange={(newValue) => {
                        setEndDate(newValue)
                        setSelectedQuickOption(null) // 手動修改日期時清除快速選擇
                      }}
                      format="yyyy/MM/dd"
                      views={['year', 'month', 'day']}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          sx: {
                            '& .MuiInputBase-input': {
                              padding: '6px 8px',
                              fontSize: '0.875rem',
                            },
                          },
                        },
                        toolbar: {
                          toolbarFormat: 'yyyy/MM/dd',
                        },
                        calendarHeader: {
                          format: 'yyyy/MM',
                        },
                      }}
                    />
                  </Box>

                  {/* 時間選擇器 - 班別日期查詢時隱藏 */}
                  {queryType !== 'dutyDate' && (
                  <Box sx={{ flex: 1, display: 'flex', gap: 0.5, minWidth: 0 }}>
                    <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                      <Select
                        value={endHour}
                        onChange={(e) => setEndHour(e.target.value)}
                        displayEmpty
                        sx={{ minWidth: 0 }}
                      >
                        {Array.from({ length: 24 }, (_, i) => {
                          const hour = i.toString().padStart(2, '0')
                          return <MenuItem key={hour} value={hour}>{hour}</MenuItem>
                        })}
                      </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, fontSize: '1.2rem', fontWeight: 600, flexShrink: 0 }}>:</Box>
                    <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                      <Select
                        value={endMinute}
                        onChange={(e) => setEndMinute(e.target.value)}
                        displayEmpty
                        sx={{ minWidth: 0 }}
                      >
                        {Array.from({ length: 60 }, (_, i) => {
                          const minute = i.toString().padStart(2, '0')
                          return <MenuItem key={minute} value={minute}>{minute}</MenuItem>
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                  )}
                </Box>
              </Box>
            </LocalizationProvider>

            {/* 查詢按鈕 */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              sx={{
                backgroundColor: '#6B705C',
                py: 1.5,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#5A5F4A',
                },
              }}
            >
              查詢
            </Button>
          </Collapse>
        </Paper>
        )}

        {/* 單日報表總結 - 僅班別日期查詢且單日時顯示 */}
        {queryType === 'dutyDate' && queriedIsSingleDay && report && (
          <Paper elevation={2} sx={{ p: 1.5, mb: 1.5, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#6B705C' }}>
              {report.queryRange.startDate} 總結
            </Typography>

            {/* 基本資訊 - 兩欄佈局 */}
            <Grid container spacing={1} sx={{ mb: 1 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.85rem' }}>姓名: </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }} component="span">{driver?.driverName || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.85rem' }}>車號: </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }} component="span">{report?.summary?.vehicleNos?.length ? report.summary.vehicleNos.join(', ') : '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.85rem' }}>市區趟數: </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }} component="span">{report.summary.localTasks}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.85rem' }}>跨區趟數: </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }} component="span">{report.summary.crossAreaTasks}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }} />

            {/* 統計資訊 - 兩欄佈局 */}
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SpeedIcon sx={{ color: '#6B705C', fontSize: 16 }} />
                  <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.8rem' }}>里程: </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#333', fontSize: '0.95rem' }} component="span">
                    {report.summary.driveMileage != null ? `${report.summary.driveMileage}km` : '-'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AssignmentIcon sx={{ color: '#6B705C', fontSize: 16 }} />
                  <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.8rem' }}>總趟數: </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#333', fontSize: '0.95rem' }} component="span">
                    {report.summary.totalTasks}趟
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocalGasStationIcon sx={{ color: '#6B705C', fontSize: 16 }} />
                  <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.8rem' }}>油耗: </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#333', fontSize: '0.95rem' }} component="span">
                    {report.summary.oilConsumption != null ? `${report.summary.oilConsumption.toFixed(1)}L` : '-'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocalGasStationIcon sx={{ color: '#6B705C', fontSize: 16 }} />
                  <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.8rem' }}>平均: </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#333', fontSize: '0.95rem' }} component="span">
                    {report.summary.avgOil != null ? `${report.summary.avgOil.toFixed(2)} km/L` : '-'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* 任務清單 - 表格呈現 */}
        {report && report.tasks.length > 0 && (
          <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 1.5, backgroundColor: '#f5f5f5' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#6B705C' }}>
                托運紀錄 ({report.pagination.totalItems})
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 300 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#fafafa' }}>
                    {!queriedIsSingleDay && (
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, px: 1, color: '#6B705C' }}>
                        日期
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, px: 1, color: '#6B705C' }}>
                      船名
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, px: 1, color: '#6B705C' }}>
                      櫃號
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, px: 1, color: '#6B705C' }}>
                      起→往
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, px: 1, color: '#6B705C' }}>
                      時間
                    </TableCell>
                    <TableCell sx={{ width: 30, py: 1, px: 0.5 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.tasks.map((task) => {
                    const isPendingAmendment = task.source === 'AMENDMENT_PENDING'
                    const isClickable = task.taskType !== 'MY' && !isPendingAmendment
                    return (
                      <TableRow
                        key={task.taskId}
                        hover={isClickable}
                        onClick={() => handleOpenTaskDetail(task)}
                        sx={{
                          cursor: isClickable ? 'pointer' : 'default',
                          backgroundColor: isPendingAmendment ? '#FFF8E7' : 'inherit',
                          '&:hover': {
                            backgroundColor: isClickable ? '#f5f5f5' : (isPendingAmendment ? '#FFF8E7' : 'inherit'),
                          },
                        }}
                      >
                        {!queriedIsSingleDay && task.acceptedTime && (
                          <TableCell sx={{ fontSize: '0.8rem', py: 1, px: 1, whiteSpace: 'nowrap', fontWeight: 500, color: '#333' }}>
                            {new Date(task.acceptedTime).toLocaleDateString('zh-TW', {
                              month: '2-digit',
                              day: '2-digit',
                            })}
                          </TableCell>
                        )}
                        <TableCell sx={{ fontSize: '0.85rem', py: 1, px: 1, fontWeight: 500, color: '#333' }}>
                          {isPendingAmendment && (
                            <Chip
                              label="補登審核中"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                bgcolor: '#F4A261',
                                color: '#fff',
                                mr: 0.5,
                                mb: 0.3,
                                '& .MuiChip-label': { px: 0.6 },
                              }}
                            />
                          )}
                          {task.vesselName || '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', py: 1, px: 1, fontFamily: 'monospace', fontWeight: 500, color: '#333' }}>
                          {task.containerNo || '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.85rem', py: 1, px: 1, whiteSpace: 'nowrap', fontWeight: 500, color: '#333' }}>
                          {task.originShortName || task.origin || '-'}→{task.destinationShortName || task.destination || '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', py: 1, px: 1, whiteSpace: 'nowrap', fontWeight: 500, color: '#333' }}>
                          {isPendingAmendment ? (
                            <Typography variant="caption" sx={{ color: '#F4A261', fontWeight: 600 }}>
                              審核中
                            </Typography>
                          ) : task.acceptedTime && task.completedTime ? (
                            <>
                              {format(new Date(task.acceptedTime), 'HH:mm')}
                              <br />
                              {format(new Date(task.completedTime), 'HH:mm')}
                            </>
                          ) : '-'}
                        </TableCell>
                        <TableCell sx={{ width: 30, py: 1, px: 0.5, textAlign: 'center' }}>
                          {isClickable && (
                            <ChevronRightIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {/* 手機友善的分頁控制項 */}
            <Box
              sx={{
                borderTop: '1px solid #e0e0e0',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'center',
              }}
            >
              {/* 頁碼資訊與每頁筆數選擇 */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Typography variant="body2" sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
                  第 {(report.pagination.page - 1) * report.pagination.pageSize + 1}-{Math.min(report.pagination.page * report.pagination.pageSize, report.pagination.totalItems)} 筆 / 共 {report.pagination.totalItems} 筆
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setRowsPerPageDialogOpen(true)}
                  sx={{
                    fontSize: '0.9rem',
                    py: 0.8,
                    px: 2,
                    borderColor: '#6B705C',
                    color: '#6B705C',
                    '&:hover': {
                      borderColor: '#6B705C',
                      bgcolor: 'rgba(107, 112, 92, 0.08)',
                    },
                  }}
                >
                  每頁 {rowsPerPage} 筆
                </Button>
              </Box>

              {/* 兩行式分頁控制 */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                {/* 第一行：可點擊的頁碼顯示 */}
                <Button
                  variant="outlined"
                  onClick={() => setPageSelectDialogOpen(true)}
                  sx={{
                    minWidth: 140,
                    fontSize: '1rem',
                    py: 1.2,
                    fontWeight: 600,
                    borderColor: '#6B705C',
                    color: '#6B705C',
                    '&:hover': {
                      borderColor: '#6B705C',
                      backgroundColor: 'rgba(107, 112, 92, 0.08)',
                    },
                  }}
                >
                  第 {report.pagination.page} / {report.pagination.totalPages} 頁
                </Button>

                {/* 第二行：分頁按鈕 */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {/* 第一頁按鈕 */}
                  <IconButton
                    disabled={page === 0}
                    onClick={(e) => handleChangePage(e, 0)}
                    sx={{
                      color: '#6B705C',
                      border: '1px solid #6B705C',
                      '&:hover': {
                        backgroundColor: 'rgba(107, 112, 92, 0.08)',
                      },
                      '&.Mui-disabled': {
                        borderColor: '#e0e0e0',
                        color: '#bdbdbd',
                      },
                    }}
                  >
                    <FirstPageIcon />
                  </IconButton>

                  {/* 上一頁按鈕 */}
                  <Button
                    variant="outlined"
                    startIcon={<NavigateBeforeIcon />}
                    disabled={page === 0}
                    onClick={(e) => handleChangePage(e, page - 1)}
                    sx={{
                      minWidth: 80,
                      fontSize: '0.9rem',
                      py: 1,
                      borderColor: '#6B705C',
                      color: '#6B705C',
                      '&:hover': {
                        borderColor: '#6B705C',
                        backgroundColor: 'rgba(107, 112, 92, 0.08)',
                      },
                      '&.Mui-disabled': {
                        borderColor: '#e0e0e0',
                        color: '#bdbdbd',
                      },
                    }}
                  >
                    上一頁
                  </Button>

                  {/* 下一頁按鈕 */}
                  <Button
                    variant="outlined"
                    endIcon={<NavigateNextIcon />}
                    disabled={report.pagination.page >= report.pagination.totalPages}
                    onClick={(e) => handleChangePage(e, page + 1)}
                    sx={{
                      minWidth: 80,
                      fontSize: '0.9rem',
                      py: 1,
                      borderColor: '#6B705C',
                      color: '#6B705C',
                      '&:hover': {
                        borderColor: '#6B705C',
                        backgroundColor: 'rgba(107, 112, 92, 0.08)',
                      },
                      '&.Mui-disabled': {
                        borderColor: '#e0e0e0',
                        color: '#bdbdbd',
                      },
                    }}
                  >
                    下一頁
                  </Button>

                  {/* 最後一頁按鈕 */}
                  <IconButton
                    disabled={report.pagination.page >= report.pagination.totalPages}
                    onClick={(e) => handleChangePage(e, report.pagination.totalPages - 1)}
                    sx={{
                      color: '#6B705C',
                      border: '1px solid #6B705C',
                      '&:hover': {
                        backgroundColor: 'rgba(107, 112, 92, 0.08)',
                      },
                      '&.Mui-disabled': {
                        borderColor: '#e0e0e0',
                        color: '#bdbdbd',
                      },
                    }}
                  >
                    <LastPageIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Paper>
        )}

        {/* 無資料提示 */}
        {report && report.tasks.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="body1" color="text.secondary">
              查詢期間無托運紀錄
            </Typography>
          </Paper>
        )}

        {/* 任務詳情對話框 */}
        <Dialog
          open={taskDetailDialogOpen}
          onClose={handleCloseTaskDetail}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: '90vh',
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                任務詳情
              </Typography>
              {selectedTask && (
                <Chip
                  label={selectedTask.statusName}
                  size="small"
                  sx={{
                    backgroundColor: '#6B705C',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                  }}
                />
              )}
            </Box>
            <IconButton onClick={handleCloseTaskDetail} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ py: 1.5 }}>
            {loadingDetail && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} sx={{ color: '#6B705C' }} />
              </Box>
            )}
            {!loadingDetail && selectedTask && (
              <Box>
                {/* 需求時間（整合基本資料、貨櫃資訊、運輸資訊） */}
                <Paper elevation={0} sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#6B705C' }}>
                    需求時間
                  </Typography>
                  <Grid container spacing={0.5}>
                    {/* 時間：接單 ~ 完成 */}
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }}>
                        {selectedTask.acceptedTime ? format(new Date(selectedTask.acceptedTime), 'M/d HH:mm') : '-'} ~ {selectedTask.completedTime ? format(new Date(selectedTask.completedTime), 'M/d HH:mm') : '-'}
                      </Typography>
                    </Grid>
                    {/* 起訖點：[起點] → [訖點] */}
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.85rem' }}>起訖點: </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }} component="span">
                        {selectedTask.originShortName || selectedTask.origin || '-'} → {selectedTask.destinationShortName || selectedTask.destination || '-'}
                      </Typography>
                    </Grid>
                    {/* 卸貨港（僅出口顯示）- 暫時隱藏 */}
                    {/* {selectedTask.ioTypeName === '出口' && selectedTask.dischargePort && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.85rem' }}>卸貨港: </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }} component="span">
                          {selectedTask.dischargePort}
                        </Typography>
                      </Grid>
                    )} */}
                    {/* 貨櫃摘要：[空重櫃] [櫃長] */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip label={selectedTask.emptyLoadedName || '-'} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.85rem' }} />
                        <Chip label={selectedTask.containerSize ? `${selectedTask.containerSize} 呎` : '-'} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.85rem' }} />
                      </Box>
                    </Grid>
                    {/* 貨櫃號碼 */}
                    {selectedTask.containerNo && (
                      <Grid item xs={12}>
                        <Chip label={selectedTask.containerNo} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.85rem' }} />
                      </Grid>
                    )}
                  </Grid>
                </Paper>

                <Paper elevation={0} sx={{ p: 1.5, mt: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', mb: 0.5 }}>貨主資訊</Typography>
                      <Box sx={{ backgroundColor: '#fff', p: 1, borderRadius: 1, border: '1px solid #e0e0e0', minHeight: 40 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: '#333', whiteSpace: 'pre-wrap' }}>
                          {selectedTask.consignee || '-'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.85rem' }}>備註: </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }} component="span">{selectedTask.notes || '-'}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 1.5 }}>
            <Button onClick={handleCloseTaskDetail} variant="outlined" fullWidth>
              關閉
            </Button>
          </DialogActions>
        </Dialog>

        {/* 每頁筆數選擇對話框 */}
        <Dialog
          open={rowsPerPageDialogOpen}
          onClose={() => setRowsPerPageDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              position: 'fixed',
              bottom: 0,
              m: 0,
              maxHeight: '50vh',
            },
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              選擇每頁顯示筆數
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 0, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {[10, 20, 30, 50, 100].map((value) => (
                <Button
                  key={value}
                  onClick={async () => {
                    setRowsPerPage(value)
                    setPage(0)
                    setRowsPerPageDialogOpen(false)
                    // 重新呼叫 API 取得資料
                    if (startDate && endDate) {
                      try {
                        setLoading(true)
                        const response = await getDailyReport({
                          startDate: format(startDate, 'yyyy-MM-dd'),
                          endDate: format(endDate, 'yyyy-MM-dd'),
                          startTime: `${startHour}:${startMinute}`,
                          endTime: `${endHour}:${endMinute}`,
                          queryType,
                          page: 1, // 每頁筆數變更時回到第一頁
                          pageSize: value,
                        })
                        setReport(response)
                      } catch (error) {
                        console.error('載入日報表失敗:', error)
                      } finally {
                        setLoading(false)
                      }
                    }
                  }}
                  sx={{
                    py: 3,
                    fontSize: '1.2rem',
                    fontWeight: rowsPerPage === value ? 700 : 400,
                    color: rowsPerPage === value ? '#6B705C' : '#333',
                    bgcolor: rowsPerPage === value ? 'rgba(107, 112, 92, 0.1)' : 'transparent',
                    borderBottom: '1px solid #e0e0e0',
                    borderRadius: 0,
                    justifyContent: 'center',
                    '&:hover': {
                      bgcolor: 'rgba(107, 112, 92, 0.15)',
                    },
                  }}
                >
                  {value} 筆
                  {rowsPerPage === value && (
                    <Typography component="span" sx={{ ml: 1, fontSize: '1rem', color: '#6B705C' }}>
                      ✓
                    </Typography>
                  )}
                </Button>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button
              onClick={() => setRowsPerPageDialogOpen(false)}
              variant="outlined"
              fullWidth
              sx={{
                py: 1.5,
                fontSize: '1rem',
                borderColor: '#6B705C',
                color: '#6B705C',
                '&:hover': {
                  borderColor: '#6B705C',
                  bgcolor: 'rgba(107, 112, 92, 0.08)',
                },
              }}
            >
              取消
            </Button>
          </DialogActions>
        </Dialog>

        {/* 頁碼選擇對話框 */}
        <Dialog
          open={pageSelectDialogOpen}
          onClose={() => setPageSelectDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              position: 'fixed',
              bottom: 0,
              m: 0,
              maxHeight: '60vh',
            },
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              選擇頁數
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 0, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {Array.from({ length: report?.pagination.totalPages || 0 }, (_, index) => index).map((pageIndex) => (
                <Button
                  key={pageIndex}
                  onClick={() => {
                    setPageSelectDialogOpen(false)
                    handleChangePage(null, pageIndex)
                  }}
                  sx={{
                    py: 3,
                    fontSize: '1.2rem',
                    fontWeight: (report?.pagination.page ?? 1) - 1 === pageIndex ? 700 : 400,
                    color: (report?.pagination.page ?? 1) - 1 === pageIndex ? '#6B705C' : '#333',
                    bgcolor: (report?.pagination.page ?? 1) - 1 === pageIndex ? 'rgba(107, 112, 92, 0.1)' : 'transparent',
                    borderBottom: '1px solid #e0e0e0',
                    borderRadius: 0,
                    justifyContent: 'center',
                    '&:hover': {
                      bgcolor: 'rgba(107, 112, 92, 0.15)',
                    },
                  }}
                >
                  第 {pageIndex + 1} 頁
                  {(report?.pagination.page ?? 1) - 1 === pageIndex && (
                    <Typography component="span" sx={{ ml: 1, fontSize: '1rem', color: '#6B705C' }}>
                      ✓
                    </Typography>
                  )}
                </Button>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button
              onClick={() => setPageSelectDialogOpen(false)}
              variant="outlined"
              fullWidth
              sx={{
                py: 1.5,
                fontSize: '1rem',
                borderColor: '#6B705C',
                color: '#6B705C',
                '&:hover': {
                  borderColor: '#6B705C',
                  bgcolor: 'rgba(107, 112, 92, 0.08)',
                },
              }}
            >
              取消
            </Button>
          </DialogActions>
        </Dialog>

        {/* 浮動按鈕 - 滾動到頂部 */}
        <Fab
          size="small"
          onClick={handleScrollToTop}
          onTouchStart={handleFabTouchStart}
          onTouchMove={handleFabTouchMove}
          onTouchEnd={handleFabTouchEnd}
          sx={{
            position: 'fixed',
            bottom: fabY,
            // 拖動時使用絕對位置，否則使用吸附位置
            ...(isDragging && dragPosition.x > 0
              ? {
                  left: `${dragPosition.x - 22}px`, // 減去按鈕半徑，讓中心點對齊手指
                  right: 'auto',
                }
              : fabPosition === 'left'
              ? { left: 16, right: 'auto' }
              : { right: 16, left: 'auto' }),
            width: 44,
            height: 44,
            bgcolor: '#6B705C',
            opacity: isDragging ? 0.9 : 0.7,
            boxShadow: isDragging ? '0 4px 16px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.2)',
            // 拖動時不要過渡，放開時快速吸附
            transition: isDragging
              ? 'opacity 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease'
              : 'left 0.25s cubic-bezier(0.4, 0.0, 0.2, 1), right 0.25s cubic-bezier(0.4, 0.0, 0.2, 1), opacity 0.2s ease, bottom 0.25s cubic-bezier(0.4, 0.0, 0.2, 1), box-shadow 0.2s ease, transform 0.2s ease',
            transform: isDragging ? 'scale(1.1)' : 'scale(1)',
            cursor: isDragging ? 'grabbing' : 'pointer',
            touchAction: 'none',
            '&:hover': {
              bgcolor: '#5a5d4d',
              opacity: 0.85,
            },
            zIndex: 1100,
          }}
          aria-label="回到頂部（可拖動）"
        >
          <KeyboardArrowUpIcon sx={{ fontSize: '1.5rem' }} />
        </Fab>
      </Container>
    </Box>
  )
}

export default DailyReportPage
